import { createElement, useRef, useCallback, useEffect, forwardRef, Fragment } from 'react';
import { BaseMenuItem, ItemLocation, MenuFocusNavigation } from '.';
import { Icon } from '../../Icon';
import { MenuCheck, CloseTab, DisclosureNext } from '@sas/react-svg';
import { useTruncationTooltip, isTruncated } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import bundle from '../NovaComponents-Menu-gui-icu.properties';
import { TestIdProps, postfixTestId } from '../../../util/testId';
import { useIsRTL } from '../../../locale';
import { MenuPopover } from './MenuPopover';
import { Placement } from '../../Popover';
import { renderInlineCssVars, DesignTokens, useDesignTokens } from '../../../theme';
import { InvisibleText } from '../../InvisibleText';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { setRef } from '../../../util/ref-util';
import { MenuTokens, MenuListDesignProps } from './types';
import { useKeyboardTooltip } from '../../Settings/hooks/useKeyboardTooltip';
import { useEarcon, EarconName } from '../../../hooks/useEarcon';

import cx from 'classnames';
import cn from './MenuListItem.css.json';
import './MenuListItem.css';

const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlIds = createUseHtmlId('MenuListItem');

interface MenuListItemProps extends TestIdProps {
  id?: string;
  item: BaseMenuItem;
  sectionIndex: number;
  index: number;
  listIndex: number;
  menuLevel: MenuListDesignProps['menuLevel'];
  disabled?: boolean;
  closable?: boolean;
  onSelected: (location: ItemLocation[], key?: string) => void;
  onClosed?: (location: ItemLocation[]) => void;
  onDismiss?: () => void;
  onItemHovered?: MenuFocusNavigation['onItemHovered'];
  onSubmenuClosed: MenuFocusNavigation['onSubmenuClosed'];
  focusLocation: MenuFocusNavigation['focusLocation'];
  showFocus: boolean;
  isRadio?: boolean;
  additionalAriaText?: string;
  /**
   * If the parent List has any items that contain indentation then
   * the indentation marker is needed, even if the indentation level is 0
   */
  menuHasIndentation?: boolean;
  tokens?: MenuTokens;
  hasMenuItemRightIcon?: boolean;
  onEscape: (event: React.KeyboardEvent) => void;
  onSpace: (event: React.KeyboardEvent) => void;
}

/**
 * @internal
 */
export const MenuListItem = forwardRef<HTMLLIElement, MenuListItemProps>(function MenuListItem(
  props,
  forwardedRef
) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const liRef = useRef<HTMLLIElement>(null);
  const isKeyDownRef = useRef(false);

  const isRTL = useIsRTL();
  const localize = useTextLocalizer();
  const truncationMainTextRef = useTruncationTooltip();
  const truncationMainTextHtmlRef = useRef<HTMLSpanElement | null>(null);
  const truncationSecondaryTextRef = useTruncationTooltip();
  const truncationSecondaryTextHtmlRef = useRef<HTMLSpanElement | null>(null);
  const dt = useDesignTokens();
  const earcon = useEarcon({ source: EarconName.Error });

  // various element ids that will be pointed to by ariaLabelled/Describedby
  const [
    customIconMarkerId,
    indentationMarkerId,
    textMarkerId,
    additionalAriaTextMarkerId,
    deleteInfoMarkerId,
    infoTextId,
  ] = useHtmlIds(6);

  const { index, sectionIndex, onSelected, onClosed, onItemHovered, showFocus, item } = props;

  const getTooltip = () => {
    const basicTooltips = [];
    const fullTooltips = [];

    if (item.icon && 'tooltip' in item.icon && item.icon.tooltip)
      basicTooltips.push(item.icon.tooltip);
    if (
      basicTooltips.length || // indicates that item.icon.tooltip exists.
      (truncationMainTextHtmlRef.current && isTruncated(truncationMainTextHtmlRef.current)) ||
      (truncationSecondaryTextHtmlRef.current &&
        isTruncated(truncationSecondaryTextHtmlRef.current))
    ) {
      if (item.text) basicTooltips.push(item.text);
      if (item.info?.infoText) basicTooltips.push(item.info.infoText);
    }
    if (basicTooltips.length) fullTooltips.push(basicTooltips.join(', '));

    if (item.tooltip) fullTooltips.push(item.tooltip);
    if (item.closable && !item.disabled) fullTooltips.push(localize('item.delete.tooltip.label'));

    return fullTooltips.join('\n');
  };
  const setItemDeletableKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: getTooltip(),
  });

  const onListItemSelected = useCallback(() => {
    if (item.subSections || props.disabled) return;
    onSelected([[sectionIndex, index]], item.key);
  }, [onSelected, sectionIndex, index, item, props.disabled]);

  const onSubItemSelected = useCallback(
    (subItemLocation: any, key?: any) => {
      // Select a chain of items consisting of this item followed by
      // the items that were selected in the sub-menu
      onSelected([[sectionIndex, index], ...subItemLocation], key);
    },
    [onSelected, sectionIndex, index]
  );

  const onMenuItemClosed = useCallback(() => {
    if (item.subSections || props.disabled || !item.closable || !onClosed) return;
    onClosed([[sectionIndex, index]]);
  }, [onClosed, sectionIndex, index, item, props.disabled]);

  const onSubItemClosed = useCallback(
    (subItemLocation: any) => {
      if (!onClosed) return;
      onClosed([[sectionIndex, index], ...subItemLocation]);
    },
    [onClosed, sectionIndex, index]
  );

  const onHover = useCallback(() => {
    // Focus this list item
    onItemHovered?.([[sectionIndex, index]]);
  }, [onItemHovered, sectionIndex, index]);

  const onSubItemHovered = useCallback(
    (subItemLocation: any) => {
      // Focus a chain of items consisting of this item followed by
      // the items that were focused in the sub-menu
      onItemHovered?.([[sectionIndex, index], ...subItemLocation]);
    },
    [onItemHovered, sectionIndex, index]
  );

  const [current, ...rest] = props.focusLocation || [];
  const isInFocusChain = !!current && current[0] === sectionIndex && current[1] === index;
  // This item is focused if it is the only item in the focus chain
  const isFocused = isInFocusChain && props.focusLocation!.length === 1;
  const focused = isFocused && showFocus;

  useEffect(() => {
    focused && liRef.current?.focus();
  }, [focused]);

  // If navigating with the keyboard, the menu should be open if one of its items gets focus
  // If navigating with the mouse, the menu should be open if this item is hovered.
  const subSections = item.subSections;
  let hasSubMenu = !!subSections && !!subSections.length;
  if (subSections && hasSubMenu) {
    hasSubMenu =
      hasSubMenu &&
      subSections.some((subSection) => subSection.title || subSection.items.length > 0);
  }
  const isSubMenuOpen = showFocus
    ? hasSubMenu && isInFocusChain && !isFocused
    : hasSubMenu && isInFocusChain;
  const closable = !item.subSections && item.closable;

  const selected = isInFocusChain && (showFocus || isSubMenuOpen);
  const indentationLevel = item.indentation ?? 0;
  const additionalAriaText = props.additionalAriaText ?? item.tooltip;

  const indentationMarkerText = localize('item.level.aria', {
    num: (indentationLevel + 1).toString(),
  });

  const listAriaLabelledBy = [];
  if (item.icon && 'icon' in item.icon) listAriaLabelledBy.push(customIconMarkerId);
  listAriaLabelledBy.push(textMarkerId);
  if (item.info?.infoText) listAriaLabelledBy.push(infoTextId);
  if (additionalAriaText) listAriaLabelledBy.push(additionalAriaTextMarkerId);
  if (props.menuHasIndentation) listAriaLabelledBy.push(indentationMarkerId);

  const tokenOverrides = props.tokens?.();
  const itemCssVars = renderInlineCssVars(
    {
      ...getMenuThemeTokens(dt),
      'spacing-m': dt.spacing.core.m,
      'spacing-s': dt.spacing.core.s,
      'spacing-xs': dt.spacing.core.xs,
      'spacing-xxs': dt.spacing.core.xxs,
      'indentation-level': indentationLevel,
      'font-family': tokenOverrides?.fontFamily ?? 'inherit',
      fg: tokenOverrides?.fg ?? undefined,
      'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
      'font-size-s': dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s,
      'font-size-xs': dt.enableNovaTokens ? dt.fontNova.size.xs : dt.font.size.xs,
    },
    'menu-list-item'
  );

  const closeButtonCssVars = renderInlineCssVars(getCloseIconTokens(dt), 'close-button-icon');

  // FIXME: currently, the 'menuitem' role isn't added to the menuitem, since it causes the group cannot be read by JAWS.
  return (
    <Fragment>
      <div ref={itemRef} className={cn.itemContainer} onMouseEnter={onHover}>
        <li
          id={props.id}
          aria-labelledby={listAriaLabelledBy.join(' ')}
          aria-describedby={closable ? deleteInfoMarkerId : undefined}
          title={item.tooltip}
          ref={(element) => {
            setRef(liRef, element);
            setRef(forwardedRef, element);
            if (focused) setItemDeletableKeyboardTooltipRef(element);
          }}
          tabIndex={isFocused ? 0 : -1}
          onBlur={() => {
            isKeyDownRef.current = false;
          }}
          onKeyDown={() => {
            isKeyDownRef.current = true;
          }}
          onKeyUp={(event) => {
            // ignore if keydown was not on this element
            if (!isKeyDownRef.current) return;
            isKeyDownRef.current = false;

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (item.convertKeyToMouse) {
                liRef.current?.click();
              } else {
                onListItemSelected();
              }

              if (!item.subSections?.length && props.disabled) earcon.play();
            } else if (event.key === 'Delete') {
              event.preventDefault();
              onMenuItemClosed();
              if (item.subSections || props.disabled || !item.closable || !onClosed) earcon.play();
            }
          }}
          onClick={(event) => {
            // HACK: Button calls preventDefault on onClick events
            if (!event.defaultPrevented) {
              onListItemSelected();
              event.preventDefault();
            }
          }}
          style={{ ...itemCssVars, ...closeButtonCssVars }}
          className={cx(
            cn.item,
            (hasSubMenu || closable) && cn.operationItem,
            /**
             * There are several cases about the item background color(BGC) with submenu, and they are consistent with HC menu.
             *
             * 1) When hovering the item directly, its BGC is 'hoverable' style.
             * 2) If hovering the submenu opened by 1), the above item BGC is in 'selected' style.
             * 3) When leaving the item and the entire menu in 1), the item BGC is 'selected' style.
             * 4) If leaving the submenu in 2) and the entire menu, the item BGC is still 'selected' style.
             * 5) When hovering the parent item of the submenu in 2), the item BGC becomes 'hoverable' style.
             * 6) If opening the 3rd level submenu and hovering on it, the 1st parent item and the 2nd parent item are both 'selected' style.
             */
            selected && !props.disabled && cn.selected,
            focused && cn.focused,
            !hasSubMenu && !closable && props.hasMenuItemRightIcon && cn.hasRightIcon
          )}
          data-testid={item.testId}
          aria-checked={item.checked}
          aria-disabled={props.disabled}
          aria-haspopup={hasSubMenu ? true : undefined}
          role={
            item.checked === undefined
              ? 'menuitem'
              : props.isRadio
              ? 'menuitemradio'
              : 'menuitemcheckbox'
          }
        >
          <div className={cn.itemInner}>
            {item.checked && (
              <Icon
                icon={MenuCheck}
                className={cx(cn.checkmark, cn.itemIcon)}
                tokens={() => {
                  if (tokenOverrides?.fg) return { fg: tokenOverrides.fg };
                  return isInFocusChain && (showFocus || isSubMenuOpen)
                    ? { fg: 'var(--list-item-selected-fg)' }
                    : { fg: 'var(--list-item-fg)' };
                }}
                disabled={props.disabled}
              />
            )}
            {item.icon ? (
              <Icon
                id={customIconMarkerId}
                className={cn.itemIcon}
                tokens={() => ({ fg: tokenOverrides?.fg })}
                {...('icon' in item.icon
                  ? item.icon
                  : { tooltip: '', ariaLabel: '', icon: item.icon, ariaHidden: true })}
                disabled={props.disabled}
              />
            ) : null}
            <div
              className={cx(
                cn.textContainer,
                item.info?.infoHasPriority && cn.infoHasPriority,
                item.info?.infoText && cn.hasSecondaryText
              )}
            >
              <span
                id={textMarkerId}
                ref={(ref) => {
                  truncationMainTextRef(ref);
                  truncationMainTextHtmlRef.current = ref;
                }}
                className={cx(cn.itemText, props.disabled && cn.itemDisabled)}
                data-testid={postfixTestId(props.testId, 'text')}
              >
                {item.text}
              </span>
              {item.info?.infoText ? (
                <span
                  id={infoTextId}
                  ref={(ref) => {
                    truncationSecondaryTextRef(ref);
                    truncationSecondaryTextHtmlRef.current = ref;
                  }}
                  className={cx(cn.itemSecondaryText, props.disabled && cn.itemDisabled)}
                >
                  {item.info?.infoText}
                </span>
              ) : null}
            </div>
            {props.menuHasIndentation ? (
              <InvisibleText id={indentationMarkerId}>{indentationMarkerText}</InvisibleText>
            ) : null}
            {additionalAriaText ? (
              <InvisibleText id={additionalAriaTextMarkerId}>{additionalAriaText}</InvisibleText>
            ) : null}
            {closable ? (
              <InvisibleText id={deleteInfoMarkerId}>
                {item.closableAriaText ?? localize('item.delete.aria')}
              </InvisibleText>
            ) : null}
            {hasSubMenu && (
              <Icon
                className={cn.navigationIcon}
                icon={DisclosureNext}
                tokens={() => ({ fg: tokenOverrides?.fg })}
              />
            )}
            {closable ? (
              <span className={cx(cn.closeButton, props.disabled && cn.closeButtonDisabled)}>
                <Icon
                  testId={postfixTestId(props.testId, 'closeButton')}
                  tooltip={item.closeIconTooltip ?? localize('item.close.icon.tooltip.label')}
                  ariaHidden
                  icon={CloseTab}
                  tabIndex={-1}
                  onClick={(e) => {
                    onMenuItemClosed();
                    e.stopPropagation();
                  }}
                  disabled={props.disabled}
                />
              </span>
            ) : null}
          </div>
        </li>
      </div>
      {isSubMenuOpen ? (
        <MenuPopover
          testId={postfixTestId(props.testId, 'menu')}
          maxHeight={item.submenuMaxHeight}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              props.onEscape(e);
              e.stopPropagation();
            }
            if (e.key === ' ') {
              props.onSpace(e);
              e.stopPropagation();
            }
          }}
          isOpen={isSubMenuOpen}
          isSubMenu
          disabled={props.disabled}
          sections={item.subSections!}
          referenceElementRef={itemRef}
          placement={`${isRTL ? 'left' : 'right'}-start` as Placement}
          focusLocation={rest || null}
          showFocus={showFocus}
          onItemHovered={onSubItemHovered}
          onItemSelected={onSubItemSelected}
          onItemClosed={onSubItemClosed}
          onSubmenuClosed={props.onSubmenuClosed}
          menuLevel={props.menuLevel ? props.menuLevel + 1 : 1}
        />
      ) : null}
    </Fragment>
  );
});

const getCloseIconTokens = (dt: DesignTokens) => {
  const standard = dt.enableNovaTokens ? dt.stateStandardNova : dt.stateStandard;
  return {
    'hover-fg': standard.hover.fg,
    'hover-bg': standard.hover.bg,
    'active-fg': standard.active.fg,
    'active-bg': standard.active.bg,
    'active-filter': standard.active.filter,
  };
};

const getMenuThemeTokens = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      'description-fg': dt.c.colorTextLabel,
    };
  } else {
    switch (dt.themeVariant) {
      case 'high-contrast':
        return {
          'description-fg': dt.c.white,
        };
      case 'dark':
        return {
          'description-fg': dt.c.white80,
        };
      default:
        return {
          'description-fg': dt.c.n_1,
        };
    }
  }
};
